import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Edit2, PackagePlus, Users2, Folder, FileText, Archive as ArchiveIcon } from "lucide-react";
import EditAddonDialog from "./EditAddonDialog";
import { formatPrice } from "./SubscriptionTiersTab";

// Which lucide icon to show alongside each add-on based on its target limit.
const LIMIT_ICON = {
  max_team_users: Users2,
  max_concurrent_projects: Folder,
  max_archived_projects: ArchiveIcon,
  max_documents_per_project: FileText,
};

const LIMIT_LABEL = {
  max_team_users: "Team users",
  max_concurrent_projects: "Concurrent projects",
  max_archived_projects: "Archived projects",
  max_documents_per_project: "Documents / project",
};

/**
 * Canonical add-on catalog display. The catalog is HARDCODED at the backend
 * to exactly 4 add-ons (extra_team_user, extra_concurrent_project,
 * extra_archived_project, extra_documents_pack) — sales can only edit the
 * reference unit price. Full-width tiles per user's request.
 *
 * The Documents add-on is priced per 50-document bundle (bundle_size=50 on
 * the catalog doc); every other add-on has bundle_size=1.
 */
export default function AddonsSection() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full addon or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/subscription-addons");
      setAddons(data.addons || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="addons-section">
      <div>
        <div className="font-display text-2xl font-bold text-[#2C2A29]">Add-ons</div>
        <p className="text-sm text-[#7A7571] mt-1 max-w-2xl">
          Fixed catalog of 4 add-ons that sales can layer on top of any tier.
          Each add-on lifts one specific tier limit by the quantity assigned
          to a firm. Prices are editable — the catalog itself is locked.
        </p>
      </div>

      {loading ? (
        <div className="text-[#7A7571]">Loading add-ons…</div>
      ) : addons.length === 0 ? (
        <div className="card-soft text-center py-8">
          <PackagePlus size={22} className="mx-auto text-[#7A7571] mb-2" />
          <div className="text-[#7A7571]">Add-on catalog is empty. Restart the backend to reseed the canonical add-ons.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {addons.map((a) => {
            const Icon = LIMIT_ICON[a.limit_key] || PackagePlus;
            const bundleSize = Number(a.bundle_size || 1);
            const isBundled = bundleSize > 1;
            const bundleUnitWord = a.limit_key === "max_documents_per_project"
              ? "doc"
              : a.limit_key === "max_archived_projects"
              ? "project"
              : "unit";
            const perUnitLabel = isBundled
              ? `per ${bundleSize}-${bundleUnitWord} bundle`
              : "per unit";
            const incrementLabel = isBundled
              ? `${LIMIT_LABEL[a.limit_key] || a.limit_key} (by ${bundleSize} per bundle)`
              : (LIMIT_LABEL[a.limit_key] || a.limit_key);
            return (
              <div
                key={a.addon_id}
                className="card-soft flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
                data-testid={`addon-card-${a.slug}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-[#F7F4EC] flex items-center justify-center text-[#7A7571] flex-shrink-0">
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-semibold text-[#2C2A29]">{a.name}</div>
                      {isBundled && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">
                          {bundleSize}-pack
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mt-0.5">{a.slug}</div>
                    {a.description && (
                      <p className="text-sm text-[#7A7571] mt-2 leading-relaxed">{a.description}</p>
                    )}
                    <div className="mt-2 text-xs text-[#7A7571]">
                      Increments <span className="font-semibold text-[#2C2A29]">{incrementLabel}</span>
                    </div>
                    <div className="text-xs text-[#7A7571] mt-1">
                      {a.assigned_firm_count} firm{a.assigned_firm_count === 1 ? "" : "s"} using this add-on
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-2xl font-display font-bold text-[#E07A5F]">
                      {formatPrice(a.currency, a.unit_price_cents)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">{perUnitLabel}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    data-testid={`addon-edit-${a.slug}`}
                    className="inline-flex items-center gap-1 text-xs text-[#2C2A29] hover:underline"
                  >
                    <Edit2 size={12} /> Edit price
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <EditAddonDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
